import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useTheme } from "@mui/material/styles";
import Swal from "sweetalert2";

import MasterPage from "components/modules/masterpage";
import CustomCard from "components/modules/cards";
import { Title } from "components/elements/texts";
import getPendingExamsReportService from "./services/getPendingExamsReportService";

const PendingExamsReport = () => {
  const theme = useTheme();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await getPendingExamsReportService();

      const contentDisposition = response.headers["content-disposition"] || "";
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=([^;\n]*)/);
      const filename = filenameMatch
        ? filenameMatch[1].replace(/['"]/g, "").trim()
        : "pending-exams-report";

      const contentType =
        response.headers["content-type"] || "application/octet-stream";
      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading pending exams report:", error);
      Swal.fire({
        title: "Download failed",
        text: "An error occurred while downloading the report. Please try again.",
        icon: "error",
        confirmButtonText: "Got it!",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <MasterPage>
      <CustomCard>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            marginBottom: 3,
          }}
        >
          <Title value="Pending Exams Report" color={theme.palette.primary.main} />
        </Box>

        <Stack spacing={3} maxWidth={480}>
          <Typography variant="body1" color="text.secondary">
            Download a report of all exam instances that are currently pending
            (i.e. assigned but not yet completed). The file will be downloaded
            directly to your device.
          </Typography>

          <Box>
            <Button
              variant="contained"
              startIcon={
                isDownloading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <DownloadIcon />
                )
              }
              onClick={handleDownload}
              disabled={isDownloading}
              sx={{ textTransform: "none" }}
            >
              {isDownloading ? "Downloading…" : "Download Report"}
            </Button>
          </Box>
        </Stack>
      </CustomCard>
    </MasterPage>
  );
};

export default PendingExamsReport;
